import React, { lazy, Suspense, useEffect, useState } from "react";
import ReactDOM from "react-dom/client";

import {
  Navigate,
  RouterProvider as Router,
  createHashRouter,
  useRouteError,
} from "react-router-dom";
import "./styles/tokens.css";
import "./index.css";
import "./styles/ui-kit.css";
import { SnackbarProvider, closeSnackbar } from "notistack";
import closeIcon from "./assets/icons/close-icon-white.png";
import SuspenseLoader from "./components/SuspenseLoader/SuspenseLoader";
import { isDevMode } from "./util/devMode";
import TermsAndConditions from "./pages/TermsAndConditions";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import { UserProvider, useUser } from "./context/UserContext/UserProvider";
import { NotificationsProvider } from "./context/NotificationsContext/NotificationsProvider";
import NotificationDrawer from "./components/Notifications/NotificationDrawer";
import ResetPassword from "./pages/Auth/ResetPassword/ResetPassword";
import VerifyEmail from "./pages/Auth/VerifyEmail/VerifyEmail";
import { PortalProvider } from "./context/PortalContext/portalcontext";
import { LogProvider } from "./context/LogContext/LogProvider";
import { Capacitor } from "@capacitor/core";
import Button from "./components/Button/Button";
import {
  ANDROID_DOWNLOAD_LINK,
  backendUrl,
  getMobileOperatingSystem,
  IOS_DOWNLOAD_LINK,
  version,
  OAUTH_GOOGLE_IOS_CLIENT_ID,
  OAUTH_GOOGLE_WEB_CLIENT_ID,
  OAUTH_GOOGLE_ANDROID_CLIENT_ID,
} from "./api/constants";
import { isIOS } from "./util/platformDetection";
import UnsubscribeEmail from "./pages/UnsubscribeEmail";
import GoogleOAuthCallback from "./pages/Auth/GoogleOAuthCallback/GoogleOAuthCallback";
import { IoMdClose } from "react-icons/io";
import { IoCloseOutline } from "react-icons/io5";
import axios from "axios";
import logoLight from "./assets/imgs/logo-transparent-light.gif";
import UILoader from "./components/UILoader/UILoader";
import Providers from "./Providers";
import L from "leaflet";
import { SafeArea } from "capacitor-plugin-safe-area";
import WatchList from "./components/WatchList/WatchList";
import { WatchListProvider } from "./context/WatchListContext/WatchListProvider";
import { RosterRedirect } from "./pages/roster/RosterPage";
import OAuthCallback from "./components/OAuthCallback/OAuthCallback";
import JoinRosterPage from "./pages/JoinRoster/JoinRosterPage";
import { openAppStore, openAppViaUniversalLink } from "./util/storeUtils";
// Switched from @codetrix-studio/capacitor-google-auth to @capgo/capacitor-social-login for Google
import { SocialLogin } from "@capgo/capacitor-social-login";

axios.defaults.headers.common["ngrok-skip-browser-warning"] = "6941"

// Initialize SocialLogin (Google) early so Web/Android script loads in time
if (typeof window !== 'undefined') {
  const platform = Capacitor.getPlatform();
  const googleInit = platform === 'ios'
    ? {
        google: {
          iOSClientId: OAUTH_GOOGLE_IOS_CLIENT_ID,
          // For online mode, iOSServerClientId not required; use web client if needed later
          iOSServerClientId: OAUTH_GOOGLE_WEB_CLIENT_ID,
          mode: 'online',
        },
      }
    : {
        google: {
          // Android and Web require web client id
          webClientId: OAUTH_GOOGLE_WEB_CLIENT_ID,
          mode: 'online',
        },
      };

  console.log(`[SocialLogin] Initializing Google for platform: ${platform}`);
  SocialLogin.initialize(googleInit).catch((e) => {
    console.warn('[SocialLogin] initialize error', e);
  });
}

// Log library version on startup
try {
  import('lib').then(lib => {
    if (lib.getLibraryVersion) {
      console.log(`[app] lib version: ${lib.getLibraryVersion()}`);
    } else {
      console.log('[app] lib version: unknown (getLibraryVersion not available)');
    }
  }).catch(() => {
    console.log('[app] lib version: unknown (import failed)');
  });
} catch (e) {
  console.log('[app] lib version check failed:', e?.message || e);
}
const Portal = lazy(() => import("./pages/Portal"));
const DevTools = lazy(() => import("./pages/dev/DevTools"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Protected = lazy(() => import("./pages/Protected"));
const UserSettings = lazy(() => import("./pages/UserSettings"));
const Auth = lazy(() => import("./pages/Auth"));
const Signin = lazy(() => import("./pages/Auth/Signin/Signin"));
const Signup = lazy(() => import("./pages/Auth/Signup/Signup"));

const ErrorBoundary = () => {
  const error = useRouteError();
  console.log(error);

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        height: "100vh",
        width: "100vw",
        backgroundColor: "var(--inyc-primary)",
        fontSize: "var(--inyc-text-xl)",
        textAlign: "center",
        padding: "var(--inyc-space-16) var(--inyc-space-32)",
      }}
    >
      <div className="mb-kit-8 font-['Primary-Regular'] text-kit-md">
        <span className="font-['Primary-Medium'] text-kit-lg">oops! something went wrong.</span>
        <br />
        <span className="block h-[0.7em]" />
        please refresh the page.
      </div>
      <br />
      <button
        type="button"
        className="error-refresh-pill mt-[-10px] rounded-kit-full border border-[var(--inyc-outline-color)] bg-white px-kit-16 py-kit-6 font-['Primary-Medium'] text-kit-base leading-none text-black cursor-pointer"
        onClick={() => {
          window.location.reload();
        }}
      >
        refresh
      </button>
    </div>
  );
};

const router = createHashRouter([
  {
    // hidden developer tools; only reachable after unlocking developer mode in settings
    path: "/dev",
    Component: () =>
      isDevMode() ? (
        <Suspense fallback={<SuspenseLoader />}>
          <DevTools />
        </Suspense>
      ) : (
        <Navigate to="/" replace />
      ),
  },
  {
    path: "/privacy-policy",
    Component: PrivacyPolicy,
  },
  {
    path: "/terms-and-conditions",
    Component: TermsAndConditions,
  },
  {
    path: "/unsubscribe-email",
    Component: UnsubscribeEmail,
  },
  {
    path: "/google-auth",
    Component: GoogleOAuthCallback,
  },
  {
    path: "/join-roster",
    Component: JoinRosterPage,
  },
  {
    path: "/",
    Component: () => {
      return (
        <Suspense fallback={<SuspenseLoader black />}>
          <Providers>
            <UILoader />
            <Protected />
          </Providers>
        </Suspense>
      );
    },
    children: [
      {
        path: "",
        Component: () => (
          <Suspense fallback={<SuspenseLoader />}>
            <Dashboard />
          </Suspense>
        ),
        index: true,
      },
      {
        path: "watchlist",
        Component: () => (
          <Suspense fallback={<SuspenseLoader />}>
            <WatchListProvider>
              <WatchList />
            </WatchListProvider>
          </Suspense>
        ),
      },
      {
        // rosters now live in the dashboard; keeps old links (notifications, emails) working
        path: "roster",
        Component: RosterRedirect,
      },
      {
        path: "settings",
        Component: () => (
          <Suspense fallback={<SuspenseLoader />}>
            <UserSettings noCloseButton />
          </Suspense>
        ),
      },
      {
        path: "dashboard",
        Component: () => (
          <Suspense fallback={<SuspenseLoader />}>
            <PortalProvider>
              <Portal />
            </PortalProvider>
          </Suspense>
        ),
      },
    ],
    errorElement: <ErrorBoundary />,
  },
  {
    path: "/auth",
    Component: () => (
      <Suspense fallback={<SuspenseLoader />}>
        <Auth />
      </Suspense>
    ),
    children: [
      {
        path: "signin",
        Component: () => (
          <Suspense fallback={<SuspenseLoader />}>
            <Signin />
          </Suspense>
        ),
      },
      {
        path: "signup",
        Component: () => (
          <Suspense fallback={<SuspenseLoader />}>
            <Signup />
          </Suspense>
        ),
      },
      {
        path: "reset-password",
        Component: () => (
          <Suspense fallback={<SuspenseLoader />}>
            <ResetPassword />
          </Suspense>
        ),
      },
      {
        path: "verify-email",
        Component: () => (
          <Suspense fallback={<SuspenseLoader />}>
            <VerifyEmail />
          </Suspense>
        ),
      },
      {
        path: "oauth-callback",
        Component: () => (
          <Suspense fallback={<SuspenseLoader />}>
            <OAuthCallback />
          </Suspense>
        ),
      },
      {
        path: "",
        Component: () => <Navigate to="signin" replace />,
      },
    ],
  },
  {
    path: "*",
    Component: () => <Navigate to="/" replace />,
  },
]);

const OpenApp = () => {
  const [transform, setTransform] = useState("translate(0, -50vh)");
  const [dragging, setDragging] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [startPos, setStartPos] = useState({ x: 0, y: 0 });
  const [lockDirection, setLockDirection] = useState(null);
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [updateDismissed, setUpdateDismissed] = useState(false);
  useEffect(() => {
    // Web auto-updates on reload — no update prompt needed there.
    if (!Capacitor.isNativePlatform()) return;
    //get latest version of the app and check if its the same as the current version
    const checkForUpdate = async () => {
      const { data } = await axios.get(
        `${backendUrl}/user/version?platform=${
          (Capacitor.isNativePlatform() 
            ? Capacitor.getPlatform() === "ios"
            : getMobileOperatingSystem() === "ios") ? "ios" : "android"
        }`
      );
      console.log(data);
      
      // Proper semantic version comparison
      const serverVersionParts = data.version.split('.').map(Number);
      const currentVersionParts = version.split('.').map(Number);
      
      // Compare each part of the version (major.minor.patch)
      let updateNeeded = false;
      for (let i = 0; i < Math.max(serverVersionParts.length, currentVersionParts.length); i++) {
        const serverPart = serverVersionParts[i] || 0;
        const currentPart = currentVersionParts[i] || 0;
        
        if (serverPart > currentPart) {
          updateNeeded = true;
          break;
        } else if (serverPart < currentPart) {
          break;
        }
      }
      
      if (updateNeeded) {
        setUpdateAvailable(data.version);
      }
    };
    checkForUpdate();
  }, []);

  useEffect(() => {
    const isNotNative = !Capacitor.isNativePlatform();
    const isMobile = getMobileOperatingSystem() === "ios" || getMobileOperatingSystem() === "android";
    const isWeb = Capacitor.getPlatform() === "web";

    if (isNotNative && isMobile && isWeb) {
      setTimeout(() => {
        setTransform("translate(0, 0)");
      }, 3500);
    }
  }, [Capacitor.isNativePlatform()]);

  const dismiss = (style, e) => {
    e && e.stopPropagation();
    setTransform(style || "translate(0, -150%)");
  };


  const tryOpenApp = () => {
    const url = openAppViaUniversalLink();
    console.log('[OpenApp] Redirecting to universal link:', url);
  };

  const handleDragStart = (e) => {
    document.documentElement.style.overflow = "hidden";
    document.body.style.overflow = "hidden";
    setDragging(true);
    const clientY = e.clientY || e.touches[0].clientY;
    setStartPos({ y: clientY });
  };

  const handleDragEnd = (e) => {
    setDragging(false);
    // setTimeout(() => {
    document.documentElement.style.overflow = "";
    document.body.style.overflow = "";
    // }, 200);
    if (position.y < -10) {
      dismiss("translate(0, -50vh)");
      return;
    } else {
      setPosition({ y: 0 });
    }
    setLockDirection(null);
  };

  const handleDragging = (e) => {
    if (dragging) {
      // if it has moved more than 10px in a direction then lock the direction
      const clientY = e.clientY || e.touches[0].clientY;
      const deltaY = Math.min(0, clientY - startPos.y);

      setPosition({ y: deltaY });
    }
  };

  const openAppDownload = () => {
    openAppStore();
  };

  useEffect(() => {
    const setBrowserSafeAreaVariables = () => {
      ["top", "right", "bottom", "left"].forEach((key) => {
        document.body.style.setProperty(
          `--safe-area-inset-${key}`,
          `env(safe-area-inset-${key}, 0px)`
        );
      });

      document.body.style.setProperty(
        "--inyc-bottom-space",
        "var(--safe-area-inset-bottom)"
      );
    };

    async function setViewportHeight() {
      // Check if running in a native app environment
      const isNative = Capacitor.isNativePlatform();

      if (isNative) {
        // Native app handling
        const { insets } = await SafeArea.getSafeAreaInsets();

        for (const [key, value] of Object.entries(insets)) {
          // Subtract 1px from bottom inset to prevent hairline gaps
          const adjustedValue = key === 'bottom' ? value - 2 : value;
          document.body.style.setProperty(
            `--safe-area-inset-${key}`,
            `${adjustedValue}px`
          );
        }

        document.body.style.setProperty(
          "--inyc-bottom-space",
          `var(--safe-area-inset-bottom)`
        );

        //if its not native ios
        if (!isIOS()) {
          const androidVersion = parseFloat(
            navigator.userAgent.match(/Android\s([0-9.]*)/)?.[1] || 0
          );

          if (androidVersion < 13) {
            // Android versions below 13 don't support safe areas
            document.body.style.setProperty("--safe-area-inset-bottom", `0px`);
          }
        }
      } else {
        // Browser safe areas are stable; keyboard height should never become bottom spacing.
        setBrowserSafeAreaVariables();
      }
    }

    // Update on resize and page load
    const handleResize = () => setViewportHeight();
    window.addEventListener("resize", handleResize);
    setViewportHeight();

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  return (
    <>
      {!updateDismissed && updateAvailable && (
        <div className="absolute z-[10001] w-full h-full top-0 left-0 bg-[var(--inyc-primary)] flex items-center justify-center">
          <div className="flex flex-col items-center justify-center gap-kit-32 p-kit-32 bg-[var(--inyc-primary)] max-w-[500px]">
            <div className="flex flex-col items-center gap-kit-24">
              <img
                className="h-[100px] invert-[var(--inyc-invert-black)]"
                src={logoLight}
                alt="logo"
              />
              <div className="text-[--inyc-primary-text-color] font-['Primary-Medium'] text-kit-xl text-center">
                new update available {updateAvailable && `(${updateAvailable})`}
              </div>
              <div className="text-[--inyc-primary-text-color] text-center text-kit-md leading-relaxed">
                a better experience with new features and bug fixes.
              </div>
              <div className="text-[--inyc-primary-text-color] text-center text-kit-md leading-relaxed">
                some features may not work without update.
              </div>
            </div>
            <div className="flex items-center gap-kit-16">
              <Button
                className="whitespace-nowrap"
                onClick={() => {
                  setUpdateDismissed(true);
                }}
              >
                update later
              </Button>
              <Button
                type="secondary"
                className="whitespace-nowrap"
                onClick={openAppDownload}
              >
                update now
              </Button>
            </div>
          </div>
        </div>
      )}
      {!updateAvailable && (
        <div
          onMouseDown={handleDragStart}
          onMouseMove={handleDragging}
          onMouseUp={handleDragEnd}
          onTouchStart={handleDragStart}
          onTouchMove={handleDragging}
          onTouchEnd={handleDragEnd}
          className={`dark fixed z-[200000] pt-[env(safe-area-inset-top)] w-full mx-auto shadow-md bg-[var(--inyc-notif-bg-color)] pb-0 px-kit-16 border-[1px] border-divider`}
          style={{
            transform: transform || "translate(0, -50vh)",
            top: `${lockDirection?.y ? 0 : position.y}px`,
            transition: dragging ? "none" : "0.6s",
          }}
        >
          <div
            className={`mb-kit-16 flex items-center justify-between mt-kit-16 gap-kit-8 w-[100%]`}
          >
            {/* App Icon */}
            <div className="flex-shrink-0">
              <img
                src="/icons/icon-48.webp"
                alt="App Icon"
                className="w-[40px] h-[40px] rounded-kit-sm"
              />
            </div>
            
            {/* Text Content */}
            <div className="flex h-[40px] flex-1 min-w-0 items-center px-kit-8">
              <div className="text-[var(--inyc-secondary-text-color)] text-kit-md font-['Primary-Medium'] leading-tight">
                open in our app
              </div>
            </div>
            
            {/* Open App Button */}
            <div className="flex-shrink-0 ml-kit-32">
              <Button
                className="whitespace-nowrap use-our-app-btn"
                btnClassName={
                  "!rounded-kit-xl !border-[0px] !py-kit-8 !px-kit-16 z-[1] !text-kit-xs"
                }
                onClick={tryOpenApp}
              >
                open
              </Button>
            </div>

            {/* Close Button - mouse/trackpad only; touch users swipe up */}
            <div className="hidden flex-shrink-0 [@media(hover:hover)_and_(pointer:fine)]:block">
              <div
                className="p-kit-8 cursor-pointer"
                onClick={() => dismiss()}
              >
                <IoCloseOutline
                  size={20}
                  color="var(--inyc-secondary-text-color)"
                />
              </div>
            </div>
          </div>
          <div className="mx-auto w-[50px] h-[3px] translate-y-[-4px] bg-black/40 rounded-kit-xs"></div>
        </div>
      )}
    </>
  );
};

ReactDOM.createRoot(document.getElementById("root")).render(
  <SnackbarProvider
    classes={{
      containerRoot: "z-alert",
      base: "notistack-snackbar-base",
    }}
    autoHideDuration={5000}
    action={(snackbarId) => (
      <button
        style={styles.snackbarCloseButton}
        onClick={() => closeSnackbar(snackbarId)}
      >
        <img src={closeIcon} style={styles.snackBarCloseIcon} />
      </button>
    )}
  >
    <LogProvider>
      <UserProvider>
        <NotificationsProvider>
          <OpenApp />
          <NotificationDrawer />
          <Router router={router} />
        </NotificationsProvider>
      </UserProvider>
    </LogProvider>
  </SnackbarProvider>
);

const styles = {
  snackbarCloseButton: {
    backgroundColor: "transparent",
    color: "var(--inyc-primary)",
    border: "none",
    paddingRight: "var(--inyc-space-10)",
    cursor: "pointer",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 100000,
  },
  snackBarCloseIcon: {
    height: "25px",
    width: "25px",
    objectFit: "contain",
    objectPosition: "center",
  },
};
